// src/pages/ClientDashboardPage.js
import React, { useState, useEffect } from 'react';
import {
    UserCircleIcon,
    QrCodeIcon,
    GiftIcon,
    CurrencyDollarIcon,
    ArrowRightIcon,
    ClockIcon,
    WalletIcon,
    ArrowDownTrayIcon, // Import the new icon
} from '@heroicons/react/24/outline';

// Mock data - In a real app, this would come from an API based on client ID
const mockCustomerData = {
    name: 'Thabo Mokoena',
    loyaltyProgram: {
        name: 'LoyalCoffee Rewards',
        type: 'points',
        currentPoints: 450,
        tier: 'Gold', // If tier-based program
        pointsToNextReward: 50, // Points needed for the next available reward
    },
    recentActivity: [
        { id: 1, type: 'earned', description: 'Purchase at Coffee Bean', points: 50, date: '2025-06-05' },
        { id: 2, type: 'redeemed', description: 'R20 Voucher', points: -200, date: '2025-05-28' },
        { id: 3, type: 'earned', description: 'Sign-up Bonus', points: 100, date: '2025-05-20' },
    ],
    // This QR code data would typically be the customer's unique LocalRewards ID
    qrCodeData: 'customer_id_12345_LocalRewards',
};

const ClientDashboardPage = ({ navigateTo }) => {
    const [customer, setCustomer] = useState(null);

    useEffect(() => {
        // Simulate fetching customer data
        const fetchCustomerData = async () => {
            await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call delay
            setCustomer(mockCustomerData);
        };
        fetchCustomerData();
    }, []);

    // Function to handle QR code download
    const handleDownloadQrCode = () => {
        if (!customer || !customer.qrCodeData) {
            alert('QR Code data not available.');
            return;
        }

        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(customer.qrCodeData)}`;
        const filename = `LocalRewards_QR_${customer.name.replace(/\s/g, '_')}.png`; // Dynamic filename

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = qrCodeUrl;
        link.download = filename; // Set the download attribute with a filename

        // Append to body, click, and then remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        alert('Your QR code has been downloaded!');
    };


    if (!customer) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <div className="flex flex-col items-center space-y-4">
                    <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-gray-700">Loading your loyalty dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 p-4 sm:p-6 lg:p-8">
            <div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-6 sm:p-8 space-y-6 animate-fade-in">
                <div className="text-center">
                    <UserCircleIcon className="h-16 w-16 text-indigo-600 mx-auto mb-3" />
                    <h2 className="text-2xl font-extrabold text-gray-900">
                        Hi, {customer.name}!
                    </h2>
                    <p className="mt-1 text-md text-gray-600">
                        Welcome to your {customer.loyaltyProgram.name} dashboard.
                    </p>
                </div>

                {/* Loyalty Card/Points Summary */}
                <div className="bg-indigo-600 text-white p-6 rounded-lg shadow-lg flex flex-col items-center justify-center relative overflow-hidden">
                    <WalletIcon className="absolute -top-4 -right-4 h-24 w-24 opacity-10" />
                    <p className="text-sm font-medium opacity-80">{customer.loyaltyProgram.name}</p>
                    <div className="flex items-baseline mt-2">
                        <CurrencyDollarIcon className="h-8 w-8 mr-2" />
                        <span className="text-5xl font-bold">{customer.loyaltyProgram.currentPoints}</span>
                        <span className="text-xl font-semibold ml-2">Points</span>
                    </div>
                    {customer.loyaltyProgram.type === 'tier' && (
                        <p className="text-sm mt-2">Your Tier: <span className="font-semibold">{customer.loyaltyProgram.tier}</span></p>
                    )}
                    {customer.loyaltyProgram.pointsToNextReward && (
                        <p className="text-sm mt-2 opacity-90">
                            Just <span className="font-semibold">{customer.loyaltyProgram.pointsToNextReward}</span> points away from your next reward!
                        </p>
                    )}
                </div>

                {/* QR Code for Earning / Identification */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center space-y-4">
                    <h3 className="text-xl font-bold text-gray-800">Scan to Earn & Identify</h3>
                    <p className="text-gray-600 text-sm">
                        Present this QR code to the cashier at checkout to earn points or redeem rewards.
                    </p>
                    <div className="bg-white p-4 rounded-md inline-block shadow-md">
                        {/* Using a placeholder for QR code image, in real app use a QR code library */}
                        <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(customer.qrCodeData)}`}
                            alt="QR Code for earning points"
                            className="w-36 h-36 mx-auto"
                        />
                    </div>
                    <p className="text-xs text-gray-500 break-all">{customer.qrCodeData}</p>

                    {/* Download QR Code Button */}
                    <button
                        onClick={handleDownloadQrCode}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download QR Code
                    </button>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        onClick={() => navigateTo('clientRedeemRewards')}
                        className="flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-500 hover:bg-green-600 shadow-md transition-colors duration-200"
                    >
                        <GiftIcon className="h-6 w-6 mr-2" /> Redeem Rewards
                    </button>
                    <button
                        onClick={() => navigateTo('clientActivityHistory')} // New page to be designed
                        className="flex items-center justify-center px-4 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-md transition-colors duration-200"
                    >
                        <ClockIcon className="h-6 w-6 mr-2" /> View Activity
                    </button>
                </div>

                {/* Recent Activity */}
                {customer.recentActivity.length > 0 && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 space-y-3">
                        <h3 className="text-xl font-bold text-gray-800 flex items-center">
                            <ClockIcon className="h-6 w-6 mr-2 text-gray-700" /> Recent Activity
                        </h3>
                        <ul className="space-y-2">
                            {customer.recentActivity.slice(0, 3).map((activity) => (
                                <li key={activity.id} className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">{activity.description}</span>
                                    <span className={`font-semibold ${activity.type === 'earned' ? 'text-green-600' : 'text-red-600'}`}>
                    {activity.type === 'earned' ? '+' : ''}{activity.points} pts
                  </span>
                                    <span className="text-gray-500 text-xs">{activity.date}</span>
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() => navigateTo('clientActivityHistory')}
                            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium mt-3 flex items-center"
                        >
                            View all activity <ArrowRightIcon className="h-4 w-4 ml-1" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientDashboardPage;